/**
 * Shared chrome for every public page: header (logo, Blog, GitHub, theme toggle)
 * and footer. Used by the landing page, the blog, and the retired-page notices.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { LogoLockup } from '@/components/brand';
import { ThemeToggle } from '@/components/ui';
import { GITHUB_URL } from '@/config/site';

const NAV_LINK_CLASS = 'text-sm font-medium text-text-secondary hover:text-text-primary';

export const SiteLayout = ({ children }: { children: ReactNode }) => (
  <div className="min-h-screen bg-background">
    {/* Skip link for keyboard navigation */}
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:outline-none"
    >
      Skip to main content
    </a>

    <header className="px-6 py-6">
      <div className="mx-auto flex max-w-5xl items-center justify-between">
        <Link
          to="/"
          aria-label="Kaibernetic home"
          className="rounded-control transition-opacity hover:opacity-80"
        >
          <LogoLockup />
        </Link>
        <nav aria-label="Site" className="flex items-center gap-4">
          <Link to="/blog" className={NAV_LINK_CLASS}>
            Blog
          </Link>
          <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={NAV_LINK_CLASS}>
            GitHub
          </a>
          <ThemeToggle />
        </nav>
      </div>
    </header>

    <main id="main-content">{children}</main>

    <footer className="border-t border-border px-6 py-8">
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-1 text-center text-sm text-text-tertiary">
        <p>&copy; {new Date().getFullYear()} Kaibernetic.</p>
        <p>
          <a href={GITHUB_URL} target="_blank" rel="noreferrer" className="hover:text-text-primary">
            pm-skills on GitHub
          </a>
        </p>
      </div>
    </footer>
  </div>
);
